/**
 * linker_types.js
 *
 * Single source of truth for which fragment-grow linker ("link type" /
 * "modType") identifiers are currently AVAILABLE to users.
 *
 * A large set of new linker types was added for fragment growing (amide,
 * amine, ether, thioether, ketone, sulfone, urea, sulfonamide, the
 * *-methylene chain linkers, the ring linkers, and reverse variants of the
 * asymmetric ones). All of the supporting code for every one of those types
 * is intentionally preserved:
 *   - search params:     fragment_geometry.js  `typeParamsMap`
 *   - display rendering: column_definitions.jsx `modType.getValue`
 *   - sort order:        utils.js               `mtypes`
 * so any type can be re-enabled by listing it here, with no other change.
 *
 * Only a curated subset is currently exposed. Two search backends produce
 * linkers and both must honor this allowlist:
 *   - the dataservice path computes geometry client-side in
 *     fragment_geometry.js; its `linkTypes` list is filtered through
 *     {@link filterAvailableLinkTypes}.
 *   - the bfd-server "classic" path returns server-computed modTypes that are
 *     post-filtered client-side in LigandMod.handleDisplaySuggestions via
 *     {@link isAvailableLinkType}.
 */

/**
 * Pre-existing ("old") link types. Always available and unaffected by the
 * new-linker allowlist. 'Near' is the proximity-only (find-near) pseudo-type.
 * @type {Set<string>}
 */
export const LEGACY_LINK_TYPES = new Set([
    'Near',
    'Bond', 'CXC', 'methylene',
    'ethane', 'ethylene_cis', 'ethylene_trans',
    'acetylene', 'Acetylene',
]);

/**
 * Approved subset of the new link types. To re-enable a new linker, add its
 * identifier(s) here; the supporting code already exists for every type in
 * fragment_geometry.js `typeParamsMap`. Reverse (`-rev`) variants are listed
 * explicitly so the approval is exact rather than prefix-based.
 * @type {Set<string>}
 */
export const APPROVED_NEW_LINK_TYPES = new Set([
    'amide', 'amide-rev',
    'amine',
    'ether',
    // 'triazole-123-14diG', 'triazole-123-14diG-rev',
    // 'triazole-124-13diG', 'triazole-124-13diG-rev',
    // 'triazole-124-35diG', 'triazole-124-35diG-rev',
    // 'oxadiazole-124-35diG', 'oxadiazole-124-35diG-rev',
]);

/**
 * True when a linker / modType identifier is currently available to users:
 * any legacy type, or one of the approved new types.
 * @param {string} linkType
 * @returns {boolean}
 */
export function isAvailableLinkType(linkType) {
    return LEGACY_LINK_TYPES.has(linkType)
        || APPROVED_NEW_LINK_TYPES.has(linkType);
}

/**
 * Filter an array of linker identifiers down to those currently available,
 * preserving order. Used by the dataservice geometry path so non-approved
 * new linkers are never generated.
 * @param {string[]} linkTypes
 * @returns {string[]}
 */
export function filterAvailableLinkTypes(linkTypes) {
    return linkTypes.filter(isAvailableLinkType);
}
